/**
 * Pure helpers for PriceListBulkPropertyDialog (selection sync, pagination,
 * Romanian numeric input sanitization).
 */

/**
 * Pagination options for the bulk dialog product table.
 * Intentionally omits Vuetify's default `-1` / "All" — rendering every matched
 * row plus pricing preview freezes the UI on large price lists.
 */
export const BULK_DIALOG_ITEMS_PER_PAGE_OPTIONS = Object.freeze([
    { value: 10, title: '10' },
    { value: 25, title: '25' },
    { value: 50, title: '50' },
    { value: 100, title: '100' },
]);

/** Debounce (ms) before rebuilding the expensive per-product pricing preview. */
export const BULK_DIALOG_PREVIEW_DEBOUNCE_MS = 250;

/** Debounce (ms) before reloading candidate products from the price-list API. */
export const BULK_CANDIDATE_FETCH_DEBOUNCE_MS = 300;

/** Max fraction digits for bulk „Valoare nouă” / % inputs. */
export const BULK_NUMERIC_INPUT_MAX_DECIMALS = 2;

/**
 * Sanitize bulk numeric input to Romanian style:
 * - decimal separator is comma only (periods are stripped — never kept as decimals)
 * - at most `maxDecimals` digits after the comma
 * - optional leading minus
 *
 * Allows incomplete typing states: "", "-", ",", "-,".
 *
 * @param {unknown} raw
 * @param {{ maxDecimals?: number, allowNegative?: boolean }} [options]
 * @returns {string}
 */
export function sanitizeBulkNumericInput(
    raw,
    {
        maxDecimals = BULK_NUMERIC_INPUT_MAX_DECIMALS,
        allowNegative = true,
    } = {},
) {
    if (raw === null || raw === undefined) {
        return '';
    }
    let text = String(raw).replace(/\s/g, '');
    if (!text) {
        return '';
    }

    let negative = false;
    if (text.startsWith('-')) {
        if (!allowNegative) {
            text = text.slice(1);
        } else {
            negative = true;
            text = text.slice(1);
        }
    }

    // Period is not allowed as decimal separator. On paste of EN values like
    // "12.5", treat a single trailing/fractional dot as comma; otherwise strip.
    if (text.includes('.') && !text.includes(',')) {
        const dotCount = (text.match(/\./g) || []).length;
        if (dotCount === 1) {
            text = text.replace('.', ',');
        } else {
            text = text.replace(/\./g, '');
        }
    } else {
        text = text.replace(/\./g, '');
    }

    // Keep digits and commas only; collapse to a single decimal comma.
    text = text.replace(/[^\d,]/g, '');
    const commaIndex = text.indexOf(',');
    if (commaIndex >= 0) {
        const intPart = text.slice(0, commaIndex).replace(/,/g, '');
        const fracPart = text
            .slice(commaIndex + 1)
            .replace(/,/g, '')
            .slice(0, Math.max(0, maxDecimals));
        text = `${intPart},${fracPart}`;
    } else {
        text = text.replace(/,/g, '');
    }

    if (!text) {
        return negative ? '-' : '';
    }
    return negative ? `-${text}` : text;
}

/**
 * Whether a keydown should be blocked for bulk numeric inputs
 * (period forbidden; max 2 decimals after comma).
 *
 * @param {KeyboardEvent} event
 * @param {string} currentValue
 * @returns {boolean} true = preventDefault
 */
export function shouldBlockBulkNumericKey(event, currentValue = '') {
    const key = event.key;
    if (!key) {
        return false;
    }
    // Allow navigation / editing / shortcuts.
    if (
        key === 'Backspace'
        || key === 'Delete'
        || key === 'Tab'
        || key === 'Enter'
        || key === 'Escape'
        || key === 'ArrowLeft'
        || key === 'ArrowRight'
        || key === 'ArrowUp'
        || key === 'ArrowDown'
        || key === 'Home'
        || key === 'End'
        || event.ctrlKey
        || event.metaKey
        || event.altKey
    ) {
        return false;
    }
    // Period / numpad decimal — never allowed (only comma).
    if (key === '.' || key === 'Decimal') {
        return true;
    }
    // Non-character keys (F1, etc.)
    if (key.length !== 1) {
        return false;
    }
    if (!/[\d,-]/.test(key)) {
        return true;
    }
    const current = String(currentValue ?? '');
    const target = event.target;
    const start = typeof target?.selectionStart === 'number'
        ? target.selectionStart
        : current.length;
    const end = typeof target?.selectionEnd === 'number'
        ? target.selectionEnd
        : current.length;
    const proposed = `${current.slice(0, start)}${key}${current.slice(end)}`;
    const sanitized = sanitizeBulkNumericInput(proposed);
    // Key would be dropped by sanitizer (e.g. 3rd decimal digit) → block.
    return sanitized === current && proposed !== current;
}

/**
 * Sync checkbox selection when the matched product set changes (filters).
 *
 * Never auto-checks rows: keep only previously selected codes that are still
 * in the matched set. User must select explicitly (or use "Selectează toate").
 *
 * @param {string[]} selectedCodes
 * @param {Array<{ product_code?: string }>} matchedItems
 * @returns {string[]}
 */
export function syncBulkMatchedSelection(selectedCodes, matchedItems) {
    const codeSet = new Set(
        (matchedItems || [])
            .map((item) => item?.product_code)
            .filter(Boolean),
    );
    const selected = Array.isArray(selectedCodes) ? selectedCodes : [];
    return selected.filter((code) => codeSet.has(code));
}

/**
 * Map bulk dialog criteria onto price-list items API query params.
 * Filters the API cannot express (multi-system/chapter, extra fields, …) stay client-side.
 *
 * @param {Record<string, unknown>} criteria
 * @returns {Record<string, string|number|boolean>}
 */
export function buildBulkCandidateQueryParams(criteria = {}) {
    const c = criteria || {};
    const params = {
        exclude_status: 'anulat',
    };
    const search = String(c.search || '').trim();
    if (search) {
        params.search = search;
    }
    const furnizor = String(c.furnizorLabel || '').trim();
    if (furnizor) {
        params.furnizor = furnizor;
    }
    const manufacturer = String(c.manufacturerCode || '').trim();
    if (manufacturer) {
        params.manufacturer = manufacturer;
    }
    const systems = (
        Array.isArray(c.systems) ? c.systems : (c.systems ? [c.systems] : [])
    )
        .map((entry) => String(entry || '').trim())
        .filter(Boolean);
    if (systems.length === 1) {
        params.system = systems[0];
    }
    const chapters = (
        Array.isArray(c.chapterCode) ? c.chapterCode : (c.chapterCode ? [c.chapterCode] : [])
    )
        .map((entry) => String(entry || '').trim())
        .filter(Boolean);
    if (chapters.length === 1) {
        params.chapter_code = chapters[0];
    }
    const subchapter = String(c.subchapterCode || '').trim();
    if (subchapter && chapters.length === 1) {
        params.subchapter_code = subchapter;
    }
    return params;
}

/**
 * True when bulk should load the full API-filtered set and paginate locally.
 * Any active filter (esp. furnizor) uses this path so matching is never limited
 * to a single server page / the Produse table page.
 *
 * @param {Record<string, unknown>} criteria
 * @returns {boolean}
 */
export function bulkCriteriaNeedsClientPagination(criteria = {}) {
    const params = buildBulkCandidateQueryParams(criteria);
    // exclude_status alone = browse full list with server pagination.
    if (Object.keys(params).some((key) => key !== 'exclude_status')) {
        return true;
    }
    const c = criteria || {};
    const systems = (
        Array.isArray(c.systems) ? c.systems : (c.systems ? [c.systems] : [])
    ).filter((entry) => String(entry || '').trim());
    if (systems.length > 1) {
        return true;
    }
    if (String(c.systemSubcategory || '').trim()) {
        return true;
    }
    const chapters = (
        Array.isArray(c.chapterCode) ? c.chapterCode : (c.chapterCode ? [c.chapterCode] : [])
    ).filter((entry) => String(entry || '').trim());
    if (chapters.length > 1) {
        return true;
    }
    if (String(c.measurementUnit || '').trim()) {
        return true;
    }
    for (const [key, value] of Object.entries(c)) {
        if ([
            'search',
            'systems',
            'systemSubcategory',
            'measurementUnit',
            'manufacturerCode',
            'manufacturerName',
            'furnizorLabel',
            'chapterCode',
            'subchapterCode',
        ].includes(key)) {
            continue;
        }
        if (value === null || value === undefined || value === '') {
            continue;
        }
        if (Array.isArray(value) && !value.length) {
            continue;
        }
        return true;
    }
    return false;
}

/**
 * Criteria still applied client-side after an API candidate fetch.
 * Drops keys the API already handled so label/shape mismatches cannot wipe rows.
 *
 * @param {Record<string, unknown>} criteria
 * @returns {Record<string, unknown>}
 */
export function bulkCriteriaForClientMatch(criteria = {}) {
    const next = { ...(criteria || {}) };
    const apiParams = buildBulkCandidateQueryParams(next);
    if (apiParams.furnizor) {
        delete next.furnizorLabel;
    }
    if (apiParams.manufacturer) {
        delete next.manufacturerCode;
        delete next.manufacturerName;
    }
    if (apiParams.search) {
        delete next.search;
    }
    if (apiParams.system) {
        // Keep multi-system client filter when API only got one value.
        const systems = (
            Array.isArray(next.systems) ? next.systems : (next.systems ? [next.systems] : [])
        ).filter((entry) => String(entry || '').trim());
        if (systems.length <= 1) {
            delete next.systems;
        }
    }
    if (apiParams.chapter_code) {
        const chapters = (
            Array.isArray(next.chapterCode)
                ? next.chapterCode
                : (next.chapterCode ? [next.chapterCode] : [])
        ).filter((entry) => String(entry || '').trim());
        if (chapters.length <= 1) {
            delete next.chapterCode;
        }
    }
    if (apiParams.subchapter_code) {
        delete next.subchapterCode;
    }
    return next;
}
